/**
 * Starter layouts for every layout the scene map needs (SPEC §5.3): 2- to 6-box for
 * MAIN, `single-box` for the Single scenes and `speaker-share`. Starting points to edit,
 * not defaults the apps fall back to.
 *
 * All boxes are 16:9 (the nested CAM-n scenes are 1920×1080), `SCALE_OUTER` (fill and
 * crop to the box), anchored top-left, with an `inside-bottom-left` nameplate hint.
 */
import { LAYOUT_SCHEMA_VERSION, REFERENCE_CANVAS, type LayoutFile, type LayoutSlot } from '@ioda/shared';

export const TEMPLATE_IDS = ['2-box', '3-box', '4-box', '5-box', '6-box', 'single-box', 'speaker-share'] as const;
export type TemplateId = (typeof TEMPLATE_IDS)[number];

export const TEMPLATE_NAMES: Readonly<Record<TemplateId, string>> = {
  '2-box': 'MAIN, 2 participants',
  '3-box': 'MAIN, 3 participants',
  '4-box': 'MAIN, 4 participants',
  '5-box': 'MAIN, 5 participants',
  '6-box': 'MAIN, 6 participants',
  'single-box': 'Single speaker',
  'speaker-share': 'Speaker + Screenshare',
};

/** Semantic slot IDs for MAIN, in sheet order: the host, then guests. */
export const MAIN_SLOT_IDS = ['host', 'guest-1', 'guest-2', 'guest-3', 'guest-4', 'guest-5'] as const;

interface BoxSpec {
  slotId: string;
  x: number;
  y: number;
  width: number;
  height: number;
  nameplate?: boolean;
}

/** A top-left anchored, fill-and-crop camera slot. */
export function cameraSlot(spec: BoxSpec, order: number): LayoutSlot {
  return {
    slotId: spec.slotId,
    order,
    positionX: spec.x,
    positionY: spec.y,
    alignment: 5,
    boundsType: 'OBS_BOUNDS_SCALE_OUTER',
    boundsWidth: spec.width,
    boundsHeight: spec.height,
    cropLeft: 0,
    cropRight: 0,
    cropTop: 0,
    cropBottom: 0,
    rotation: 0,
    zIndex: 0,
    ...(spec.nameplate === false ? {} : { nameplate: { anchor: 'inside-bottom-left' as const } }),
  };
}

function layout(layoutId: TemplateId, specs: BoxSpec[], slotCount = specs.length): LayoutFile {
  return {
    schemaVersion: LAYOUT_SCHEMA_VERSION,
    layoutId,
    name: TEMPLATE_NAMES[layoutId],
    canvas: { width: REFERENCE_CANVAS.width, height: REFERENCE_CANVAS.height },
    slotCount,
    slots: specs.map(cameraSlot),
  };
}

/** Lays boxes of `width`×`height` out in rows, each row centered horizontally, block centered vertically. */
function rows(perRow: number[], width: number, height: number, gap: number): BoxSpec[] {
  const blockHeight = perRow.length * height + (perRow.length - 1) * gap;
  const top = (REFERENCE_CANVAS.height - blockHeight) / 2;
  const specs: BoxSpec[] = [];
  perRow.forEach((count, row) => {
    const rowWidth = count * width + (count - 1) * gap;
    const left = (REFERENCE_CANVAS.width - rowWidth) / 2;
    for (let i = 0; i < count; i++) {
      specs.push({ slotId: MAIN_SLOT_IDS[specs.length]!, x: left + i * (width + gap), y: top + row * (height + gap), width, height });
    }
  });
  return specs;
}

/** Builds a fresh copy of a template. */
export function createTemplate(id: TemplateId): LayoutFile {
  switch (id) {
    case '2-box':
      return layout(id, rows([2], 910, 511.875, 20));
    case '3-box':
      return layout(id, rows([3], 600, 337.5, 20));
    case '4-box':
      return layout(id, rows([2, 2], 864, 486, 20));
    case '5-box':
      return layout(id, rows([3, 2], 600, 337.5, 20));
    case '6-box':
      return layout(id, rows([3, 3], 600, 337.5, 20));
    case 'single-box':
      return layout(id, [{ slotId: 'main', x: 40, y: 22.5, width: 1840, height: 1035 }]);
    case 'speaker-share':
      return layout(id, [
        { slotId: 'share', x: 40, y: 163.125, width: 1340, height: 753.75, nameplate: false },
        { slotId: 'speaker', x: 1400, y: 163.125, width: 480, height: 270 },
      ]);
  }
}
